import { Storage } from "aws-amplify";
import {
  monitorColors,
  MonitorColorType,
} from "components/report/ChatArea/chatColors";
import { useConsoleState } from "providers/ConsoleStateProvider";
import { useEffect, useState } from "react";
import { ChatData, ChatDataType, InterviewType } from "types/graphqlapi";
import { listChatDataByInterviewId } from "utils/apis/chatData";
import { fetchInterviewerAnnounceByInterviewIdQuery } from "utils/apis/interviewerAnnounce";
import { fetchMonitorBysubjectIdQuery } from "utils/apis/monitor";
import { fetchMonitorAssignBysubjectIdQuery } from "utils/apis/monitorAssign";
import { getYMDStr } from "utils/common";

export type ClientChatData = {
  src?: string;
  mentionedMonitorList?: { id: string; name: string }[];
} & Partial<ChatData>;

//TODO: インタビュアー固定値でよいか確認
const interviewer = {
  moderator_id: "",
  monitor_id: "",
  name: "インタビュアー",
};

type Attendee = {
  moderator_id: string;
  monitor_id: string;
  name: string;
};

const getResouceFromS3 = async (url: string) => {
  const imageURL = await Storage.get(url);
  return imageURL;
};

export const getMonitorName = (attendeeList: Attendee[], monitorId: string) => {
  const monitorName = attendeeList.find(
    (attendee) => attendee.monitor_id === monitorId
  )?.name;
  if (!monitorName) return "名無しさん";
  return monitorName;
};

export const getMonitorColor = (
  attendeeList: Attendee[],
  monitorId: string
) => {
  const index = attendeeList.findIndex(
    (attendee) => attendee.monitor_id === monitorId
  );
  const colorKey = Object.keys(monitorColors)[index] as MonitorColorType;
  return monitorColors[colorKey];
};

const fetchChatData = async (interviewId: string) => {
  const fetchedDataList = await listChatDataByInterviewId(interviewId);
  const chatData = fetchedDataList.filter(
    (fetchedData) => fetchedData.type !== ChatDataType.SYSTEMLOG
  );

  let returnList: ClientChatData[] = [];
  for (const chat of chatData) {
    if (chat.type === ChatDataType.TEXT || chat.type === ChatDataType.ICON) {
      returnList = [...returnList, chat];
    } else if (chat.type === ChatDataType.FILE) {
      if (chat.file_url) {
        const src = await getResouceFromS3(chat.file_url);
        if (src) {
          returnList = [...returnList, { ...chat, src: src }];
        }
      }
    }
  }
  return returnList;
};

export const useChatDataList = (interviewId: string, subjectId: string) => {
  const [chatDataList, setChatDataList] = useState<ClientChatData[]>([]);
  const { selectedSubjectType } = useConsoleState();

  const [monitorList, setMonitorList] = useState<Attendee[]>([interviewer]);

  const readyChatData = async () => {
    try {
      const chatDataList = await fetchChatData(interviewId);
      const interviewerAnnounceList =
        await fetchInterviewerAnnounceByInterviewIdQuery(interviewId);

      const withAnnounceList = [
        ...chatDataList,
        ...(interviewerAnnounceList as unknown as ClientChatData[]),
      ];
      withAnnounceList.sort((a, b) =>
        a.createdAt && b.createdAt && a.createdAt > b.createdAt ? 1 : -1
      );

      if (selectedSubjectType === InterviewType.TERM) {
        // 入退室履歴を排除
        const chatDataWithoutAccessList = withAnnounceList.filter(
          (chatData) => {
            if (
              chatData.type === ChatDataType.SYSTEMLOG &&
              (chatData.text == "Participated" || chatData.text === "Left")
            ) {
              return false;
            } else {
              return true;
            }
          }
        );

        const termChatDataList: ClientChatData[] =
          // チャットリストの先頭に日付のBotChatが無ければ挿入
          chatDataWithoutAccessList.length > 0 &&
          chatDataWithoutAccessList[0].text !== "Date"
            ? [
                {
                  type: ChatDataType.SYSTEMLOG,
                  text: "Date",
                  createdAt: chatDataWithoutAccessList[0].createdAt,
                },
              ]
            : [];

        const chatLength = chatDataWithoutAccessList.length;
        for (let i = 0; i < chatLength; i++) {
          termChatDataList.push(chatDataWithoutAccessList[i]);
          if (i === chatLength - 1) break;
          if (
            chatDataWithoutAccessList[i].text === "Date" ||
            chatDataWithoutAccessList[i + 1]?.text === "Date"
          )
            continue;

          const currentCreatedAt = chatDataWithoutAccessList[i].createdAt;
          const nextCreatedAt = chatDataWithoutAccessList[i + 1].createdAt;
          if (!currentCreatedAt || !nextCreatedAt) continue;

          // 前後で日付が変わっていた場合、日付のBotChatを挿入
          const currentChatDate = getYMDStr(new Date(currentCreatedAt));
          const nextChatDate = getYMDStr(new Date(nextCreatedAt));

          if (currentChatDate < nextChatDate) {
            termChatDataList.push({
              type: ChatDataType.SYSTEMLOG,
              text: "Date",
              createdAt: chatDataWithoutAccessList[i + 1].createdAt,
            });
          }
        }
        setChatDataList(termChatDataList);
      } else {
        setChatDataList(withAnnounceList);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchMonitorList = async () => {
    try {
      const monitorList = await fetchMonitorBysubjectIdQuery(subjectId);
      if (monitorList === undefined)
        throw new Error("モニター情報の取得に失敗しました");

      const monitorAssignList = await fetchMonitorAssignBysubjectIdQuery(
        subjectId
      );
      if (monitorAssignList === undefined)
        throw new Error("モニターアサイン情報の取得に失敗しました");

      const scheduledMonitorIdList = monitorAssignList.filter(
        (monitorAssign) => monitorAssign.interview_id === interviewId
      );

      const scheduledMonitorList: Attendee[] = [];
      for (const scheduledMonitor of scheduledMonitorIdList) {
        for (const monitor of monitorList) {
          if (scheduledMonitor.monitor_id === monitor.id) {
            scheduledMonitorList.push({
              moderator_id: "",
              monitor_id: monitor.id,
              name: monitor.name ? monitor.name : "名無しさん",
            });
          }
        }
      }
      scheduledMonitorList.sort((a, b) => {
        if (a.monitor_id < b.monitor_id) return -1;
        if (a.monitor_id > b.monitor_id) return 1;
        return 0;
      });

      setMonitorList((current) => [...current, ...scheduledMonitorList]);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    readyChatData();
    fetchMonitorList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { chatDataList, monitorList };
};
